import {
  Button,
  createDataTableColumnHelper,
  DataTable,
  Link as UiLink,
  SearchField,
} from "@beweegbaas/ui";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { FilterBar, type FilterBarFilter } from "#/components/filter-bar";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { getAdminScholenOverview } from "#/modules/admin/schools.functions";
import {
  SCHOOLS_ADMIN_SEARCH_DEFAULTS,
  schoolsAdminSearchSchema,
} from "#/modules/admin/schema.search";
import { getLicentieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import { m } from "#/paraglide/messages";
import { LicentieType } from "#/schema.nswag";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/schools/")({
  validateSearch: schoolsAdminSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(SCHOOLS_ADMIN_SEARCH_DEFAULTS)] },
  // Narrowed to exactly the keys the loader/server-fn uses — never the whole search object.
  loaderDeps: ({ search }) => ({
    searchTerm: search.searchTerm,
    orderBy: search.orderBy,
    orderDirection: search.orderDirection,
    page: search.page,
    pageSize: search.pageSize,
    licentieType: search.licentieType,
    licentieTypeOperator: search.licentieTypeOperator,
    verwerkersovereenkomst: search.verwerkersovereenkomst,
  }),
  loader: ({ deps }) => getAdminScholenOverview({ data: deps }),
  component: ScholenAdminOverview,
  errorComponent: SchoolsError,
});

function SchoolsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type SchoolRow = Awaited<ReturnType<typeof getAdminScholenOverview>>["scholen"][number];

const helper = createDataTableColumnHelper<SchoolRow>();

const columns = helper.columns([
  helper.accessor("name", {
    header: () => m["common.fields.name"](),
    enableSorting: true,
    meta: { rowTarget: true },
    cell: ({ row, getValue }) => (
      <UiLink
        variant="secondary"
        underline
        to="/$locale/{-$schoolyear}/admin/schools/$id/overview"
        params={(prev: Record<string, string>) => ({ ...prev, id: row.original.id })}
      >
        {getValue()}
      </UiLink>
    ),
  }),
  helper.accessor("brin", {
    header: () => m["admin.scholen.table.brin"](),
    enableSorting: true,
    cell: ({ getValue }) => getValue() || "-",
  }),
  helper.accessor("plaats", {
    header: () => m["common.fields.plaats"](),
    enableSorting: true,
    cell: ({ getValue }) => getValue() || "-",
  }),
  helper.accessor("hoofdDocent", {
    header: () => m["common.labels.hoofddocent"](),
    enableSorting: true,
    cell: ({ getValue }) => getValue() || "-",
  }),
  helper.accessor("licentieType", {
    header: () => m["admin.scholen.table.licentietype"](),
    enableSorting: true,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? getLicentieTypeTranslation(value) : "-";
    },
  }),
  helper.accessor("endDate", {
    header: () => m["admin.scholen.table.einddatum"](),
    enableSorting: true,
    cell: ({ getValue }) => {
      const endDate = getValue();
      return endDate ? new Date(endDate).toLocaleDateString("nl-NL") : "-";
    },
  }),
  // Accessor, not display: TanStack only allows sorting on columns with an accessor.
  helper.accessor("createdAt", {
    header: () => m["admin.scholen.table.aangemaakt"](),
    enableSorting: true,
    cell: ({ getValue }) => {
      const createdAt = getValue();
      return createdAt
        ? new Date(createdAt).toLocaleString("nl-NL", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "-";
    },
  }),
  helper.accessor("verwerkersovereenkomst", {
    header: () => m["admin.scholen.table.verwerkersovereenkomst"](),
    enableSorting: true,
    cell: ({ getValue }) => (getValue() ? "Ja" : "Nee"),
  }),
]);

function ScholenAdminOverview() {
  const { scholen, totalPages, totalCount } = Route.useLoaderData();
  const search = Route.useSearch();
  const overview = useOverviewSearch(search, Route.useNavigate());

  const filterBarFilters: FilterBarFilter[] = [
    {
      key: "licentieType",
      label: m["admin.scholen.table.licentietype"](),
      options: Object.values(LicentieType).map((value) => ({
        value,
        label: getLicentieTypeTranslation(value),
      })),
      selectedValues: search.licentieType,
      onValuesChange: (values) => overview.setFilter({ licentieType: values }),
      operator: search.licentieTypeOperator,
      onOperatorChange: (operator) => overview.setFilter({ licentieTypeOperator: operator }),
    },
    {
      key: "verwerkersovereenkomst",
      label: m["admin.scholen.table.verwerkersovereenkomst"](),
      options: [
        { value: "true", label: "Ja" },
        { value: "false", label: "Nee" },
      ],
      selectedValues: search.verwerkersovereenkomst,
      onValuesChange: (values) => overview.setFilter({ verwerkersovereenkomst: values }),
    },
  ];

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField className="w-auto grow md:max-w-xs" {...overview.searchFieldProps} />
        </Page.Header.SearchField>
        <Page.Header.Section>
          <FilterBar
            filters={filterBarFilters}
            onClearAll={() =>
              overview.setFilter({
                licentieType: [],
                licentieTypeOperator: "in",
                verwerkersovereenkomst: [],
              })
            }
          />
        </Page.Header.Section>
        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/admin/schools/add"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              <IconMaterialSymbolsAdd />
              School toevoegen
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label={m["common.labels.scholen"]()}
          columns={columns}
          data={scholen}
          getRowId={(school) => school.id}
          empty={m["admin.geen_scholen_gevonden"]()}
          {...overview.sortProps}
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"mb-6 border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
